import {ComponentType, FC, ReactNode, useEffect, useRef} from 'react';

import {createView, type View} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';

import {AppConfig, AppError} from '../types';
import {ComponentWithOverride} from './ComponentWithOverride';
import {ConversationScreen} from './ConversationScreen';
import {ErrorScreen} from './ErrorScreen';
import {LoadingScreen} from './LoadingScreen';
import {
  useAppError,
  useConfig,
  useConversation,
  useCurrentConversation,
} from '../hooks/appHooks';

type Props = View.IntrinsicElement<
  'div',
  {
    errorScreen?:
      | ReactNode
      | ComponentType<{config: AppConfig; error: AppError}>;
    loadingScreen?: ReactNode | ComponentType;
    log?: ReactNode | ComponentType<{conversation: Conversation}>;
    input?: ReactNode | ComponentType<{conversation: Conversation}>;
  }
>;

export const AssistantScreen: FC<View.ViewProps<Props>> = createView<Props>(
  ({errorScreen, loadingScreen, log, input, ...props}) => {
    const loadingRef = useRef<boolean>(false);
    const config = useConfig();

    const error = useAppError();

    const currentConversation = useCurrentConversation();
    const {resumeOrStartConversation} = useConversation();

    useEffect(() => {
      if (currentConversation || error) {
        loadingRef.current = false;
        return;
      }

      if (loadingRef.current) {
        return;
      }

      loadingRef.current = true;
      try {
        resumeOrStartConversation().finally(() => {
          loadingRef.current = false;
        });
      } catch (e) {
        loadingRef.current = false;
      }
    }, [currentConversation, resumeOrStartConversation, error]);

    if (error) {
      return (
        <ComponentWithOverride<{config: AppConfig; error: AppError}>
          override={errorScreen}
          component={ErrorScreen}
          config={config}
          error={error}
        />
      );
    }

    if (!currentConversation) {
      return (
        <ComponentWithOverride
          override={loadingScreen}
          component={LoadingScreen}
        />
      );
    }

    return (
      <ConversationScreen
        conversation={currentConversation}
        log={log}
        input={input}
        {...props}
      />
    );
  },
);
